import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useIdleTimer } from "react-idle-timer";
import { fetchSessionStatus, logout } from "../../api/auth";
import { redirectToAuthPortal, isPublicAuthPath } from "../../utils/authPortal";
import actions from "../../actions";
import SessionExpiryDialog from "../SessionExpiryDialog";

const KEEPALIVE_INTERVAL_MS = 60 * 1000; // poll /auth/me every 60s while active
const IDLE_TIMEOUT_MS = 1000 * 60 * 120; // 2h of inactivity → hard logout
const EXPIRY_WARNING_MS = 15 * 60 * 1000; // warn when <15 min of session remains

/**
 * Owns client-side session lifetime once the user is authenticated:
 *
 * - Keepalive: while the user is active, polls /auth/me every 60s. Each call
 *   touches the server session so an actively-used tab never lapses, and
 *   refreshes the stored expiry timestamp.
 * - Idle logout: react-idle-timer fires after 2h of inactivity → stop polling,
 *   POST /auth/logout to kill the server session immediately, redirect to portal.
 * - Expiry warning: when the session is within 15 min of expiring, show a dialog
 *   letting the user extend (one keepalive poll) or log out. Defensive — if
 *   /auth/me does not yet return expiry timestamps (an-api §C), the warning
 *   simply never triggers and keepalive still works.
 *
 * 401s on ordinary requests are handled separately by securerequest (redirect
 * to portal); this component manages the proactive/lifetime side only.
 */
const SessionManager = () => {
  const dispatch = useDispatch();
  const [warningOpen, setWarningOpen] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(null);

  // Expiry timestamp (ms epoch) from the latest /auth/me, if provided.
  const expiresAtRef = useRef(null);
  const isIdleRef = useRef(false);

  const applySessionExpiry = useCallback((result) => {
    const session = result?.session;
    const expiresAt =
      session?.refreshTokenExpiresAtUnix ?? session?.accessTokenExpiresAtUnix ?? null;
    expiresAtRef.current = typeof expiresAt === "number" ? expiresAt * 1000 : null;
  }, []);

  // Single keepalive poll: extends the server session and refreshes expiry.
  const keepAlive = useCallback(async () => {
    const status = await fetchSessionStatus();
    const { result } = status;
    if (result) {
      applySessionExpiry(result);
      // A force-password-change flag can flip on mid-session (e.g. admin reset).
      // Surface it into redux so ForcePasswordChangeGuard routes the user.
      if (result.session?.force_password_change) {
        dispatch(actions.setUserAttributes({ email: result.email, username: result.email }));
        dispatch(actions.forcePasswordChange({}));
      }
    }
    return status;
  }, [applySessionExpiry, dispatch]);

  const doLogout = useCallback(async () => {
    await logout();
    // Explicit logout (idle timeout, keepalive-detected 401, or the user
    // clicking "Log out") must always navigate away, even on a public auth
    // path like /force-password-change — the server session is already gone,
    // so staying on the page would leave the user stuck on a dead form.
    redirectToAuthPortal({ force: true });
  }, []);

  const handleIdle = useCallback(() => {
    if (isPublicAuthPath()) return;
    isIdleRef.current = true;
    setWarningOpen(false);
    // Active hard logout — don't wait for the server session to lapse.
    doLogout();
  }, [doLogout]);

  useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    onIdle: handleIdle,
    onActive: () => {
      isIdleRef.current = false;
    },
    debounce: 1000,
    crossTab: true,
    syncTimers: 500,
  });

  // Keepalive + expiry-warning loop.
  useEffect(() => {
    if (isPublicAuthPath()) return undefined;

    let cancelled = false;

    const tick = async () => {
      if (cancelled || isIdleRef.current) return;
      const { unauthorized } = await keepAlive();
      if (cancelled) return;

      // The server rejected the session (expired/revoked): leave the
      // authenticated view now rather than waiting for an unrelated request to
      // 401. Transient failures (network, 5xx) keep polling.
      if (unauthorized) {
        cancelled = true;
        clearInterval(interval);
        doLogout();
        return;
      }

      const expiresAt = expiresAtRef.current;
      if (typeof expiresAt === "number") {
        const remainingMs = expiresAt - Date.now();
        if (remainingMs <= EXPIRY_WARNING_MS && remainingMs > 0) {
          setMinutesRemaining(remainingMs / 60000);
          setWarningOpen(true);
        } else {
          setWarningOpen(false);
        }
      }
    };

    const interval = setInterval(tick, KEEPALIVE_INTERVAL_MS);
    tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [keepAlive, doLogout]);

  const handleStayLoggedIn = useCallback(async () => {
    const { result, unauthorized } = await keepAlive();
    // Only a confirmed-rejected session sends them to re-authenticate. A
    // transient failure (network, 5xx) keeps the warning open so they can
    // retry, matching the keepalive polling behavior above.
    if (unauthorized) {
      doLogout();
      return;
    }
    if (result) {
      setWarningOpen(false);
    }
  }, [keepAlive, doLogout]);

  return (
    <SessionExpiryDialog
      open={warningOpen}
      minutesRemaining={minutesRemaining}
      onStayLoggedIn={handleStayLoggedIn}
      onLogout={doLogout}
    />
  );
};

export default SessionManager;
